import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Info,
  Receipt,
} from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { FilterPresets, type FilterPreset } from '@/ui/FilterPresets';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { NextStepBanner } from '@/ui/NextStepBanner';
import { PageHeader } from '@/ui/PageHeader';
import { StatCard } from '@/ui/StatCard';
import { StatusBadge } from '@/ui/StatusBadge';
import { type TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { AcompanhamentoMensalTable } from './apuracao/AcompanhamentoMensalTable';
import { useApuracaoResumo } from './hooks/useApuracao';
import { useMedicoesLista } from './hooks/useMedicoes';
import type { DreResumoApuracao, MedicaoCicloLinha } from './types';

const ROTULO_STATUS_MEDICAO: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  PROCESSADA: 'Processada',
  APROVADA: 'Aprovada',
  EMPENHADA: 'Empenhada',
  LIQUIDADA: 'Liquidada',
  EXPORTADA_SOF: 'Exportada SOF',
  PAGA: 'Paga',
};

/** AAAA-MM do mês corrente, para abrir a tela já com uma competência escolhida. */
function mesAtual(): string {
  const agora = new Date();
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
}

function formatarMesLabel(mes: string): string {
  const [ano, mesNum] = mes.split('-').map(Number);
  if (!ano || !mesNum) return mes;
  const texto = new Date(ano, mesNum - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

type LinhaResumo = DreResumoApuracao & { id: string };

/** FIN-060/060.1: hub de apuração mensal, com apuração por DRE como aba padrão. */
export function ApuracaoMensalPage() {
  const [parametros, definirParametros] = useSearchParams();
  const navegar = useNavigate();
  const aba = parametros.get('aba') ?? 'apuracao';
  const mes = parametros.get('mes') ?? mesAtual();

  const [filtroBusca, definirFiltroBusca] = useState('');
  const [filtroStatus, definirFiltroStatus] = useState('');

  const { data, isLoading, isError, refetch } = useApuracaoResumo(mes);
  const medicoes = useMedicoesLista({ mes, page: 1, perPage: 20 });

  const todasAsLinhas: LinhaResumo[] = useMemo(
    () => (data ?? []).map((linha) => ({ ...linha, id: linha.dreId })),
    [data],
  );

  const linhas = useMemo(() => {
    const termo = filtroBusca.trim().toLowerCase();
    return todasAsLinhas.filter((r) => {
      if (termo && !`${r.dreCodigo} ${r.dreNome}`.toLowerCase().includes(termo)) return false;
      if (filtroStatus === 'concluida' && r.pendentes !== 0) return false;
      if (filtroStatus === 'pendente' && r.pendentes === 0) return false;
      return true;
    });
  }, [todasAsLinhas, filtroBusca, filtroStatus]);

  const totalOs = todasAsLinhas.reduce((s, r) => s + r.osAtivas, 0);
  const totalLancados = todasAsLinhas.reduce((s, r) => s + r.movimentosLancados, 0);
  const totalPendentes = todasAsLinhas.reduce((s, r) => s + r.pendentes, 0);
  const dresConcluidas = todasAsLinhas.filter((r) => r.pendentes === 0 && r.osAtivas > 0).length;
  const dresPendentes = todasAsLinhas.filter((r) => r.pendentes > 0).length;
  const totalFaltas = todasAsLinhas.reduce((s, r) => s + r.totalFaltas, 0);
  const totalDres = todasAsLinhas.length;
  const pctConcluidas = totalDres > 0 ? Math.round((dresConcluidas / totalDres) * 100) : 0;
  const mesLabel = formatarMesLabel(mes);

  const subtitulo = (
    <span>
      Competência <strong className="text-foreground">{mesLabel}</strong> · {totalDres} DREs ·{' '}
      <span className={pctConcluidas === 100 ? 'font-medium text-success' : 'font-medium'}>
        {pctConcluidas}% concluído
      </span>
      {totalPendentes > 0 && (
        <>
          {' · '}
          <span className="font-medium text-warning">{totalPendentes} OS pendentes</span>
        </>
      )}
    </span>
  );

  function mudarAba(novaAba: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('aba', novaAba);
      return proximo;
    });
  }

  function mudarMes(novoMes: string) {
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('mes', novoMes);
      return proximo;
    });
  }

  const camposFiltro: FilterFieldConfig[] = [
    { key: 'busca', label: 'Buscar DRE', type: 'search', placeholder: 'Código ou nome…' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      allOptionLabel: 'Todos',
      options: [
        { value: 'concluida', label: 'Concluída' },
        { value: 'pendente', label: 'Pendente' },
      ],
    },
  ];

  const colunas: TableViewColumn<LinhaResumo>[] = [
    {
      id: 'dre',
      header: 'DRE',
      sortKey: 'dreCodigo',
      cell: (r) => (
        <span className="font-medium">
          {r.dreCodigo} — {r.dreNome}
        </span>
      ),
      isCritical: true,
      canHide: false,
    },
    {
      id: 'osAtivas',
      header: 'OS Ativas',
      accessorKey: 'osAtivas',
      sortKey: 'osAtivas',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    {
      id: 'lancados',
      header: 'Lançados',
      accessorKey: 'movimentosLancados',
      sortKey: 'movimentosLancados',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    {
      id: 'pendentes',
      header: 'Pendentes',
      accessorKey: 'pendentes',
      sortKey: 'pendentes',
      cellClassName: 'text-center',
      headerClassName: 'text-center',
    },
    {
      id: 'status',
      header: 'Status',
      sortKey: (r) => (r.pendentes === 0 ? 0 : 1),
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      cell: (r) => (
        <StatusBadge
          status={r.osAtivas === 0 ? 'inativo' : r.pendentes === 0 ? 'concluida' : 'pendente'}
          label={r.osAtivas === 0 ? 'Sem OS ativa' : r.pendentes === 0 ? 'Concluída' : 'Pendente'}
        />
      ),
      isStatusColumn: true,
      isCritical: true,
      canHide: false,
      linkedFilterKeys: ['status'],
    },
  ];

  const tableView = useTableViewPreferences({
    contextKey: 'financeiro:apuracao-mensal:dre',
    columns: colunas,
    filterValues: { busca: filtroBusca, status: filtroStatus },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Apuração Mensal"
        description={
          <span>
            Hub do ciclo financeiro: consolide a apuração por{' '}
            <GlossaryTerm termo="COMPETENCIA">competência</GlossaryTerm> e acompanhe cada etapa até
            o pagamento (<GlossaryTerm termo="MEDICAO">medição</GlossaryTerm> →{' '}
            <GlossaryTerm termo="SELAMENTO">selamento</GlossaryTerm>).
          </span>
        }
      />

      <Tabs value={aba} onValueChange={mudarAba} className="space-y-4">
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="apuracao" className="gap-2">
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
            Apuração por DRE
          </TabsTrigger>
          <TabsTrigger value="acompanhamento" className="gap-2">
            <Activity className="h-4 w-4" aria-hidden="true" />
            Acompanhamento Mensal
          </TabsTrigger>
          <TabsTrigger value="medicoes" className="gap-2">
            <Receipt className="h-4 w-4" aria-hidden="true" />
            Medições
          </TabsTrigger>
        </TabsList>

        <TabsContent value="apuracao" className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">{subtitulo}</p>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                type="month"
                value={mes}
                onChange={(e) => mudarMes(e.target.value)}
                className="w-48"
                aria-label="Selecionar competência"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-info/40 bg-info/5 px-3 py-2 text-xs text-info">
            <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">
              Esta visão foi integrada ao <strong>Fechamento Mensal</strong>. Os mesmos KPIs e o
              detalhe por DRE agora ficam dentro do fechamento da competência.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              onClick={() =>
                navegar(`/operacao/financeiro/fechamento-mensal/${mes}-01__PF?mes=${mes}&tipo=PF`)
              }
            >
              Abrir Fechamento Mensal <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <StatCard title="OS Ativas" value={totalOs} icon={ClipboardCheck} />
            <StatCard title="OS Lançadas" value={totalLancados} icon={CheckCircle2} />
            <StatCard title="OS Pendentes" value={totalPendentes} icon={Clock} />
            <StatCard title="DREs Concluídas" value={dresConcluidas} icon={CheckCircle2} />
            <StatCard title="DREs Pendentes" value={dresPendentes} icon={Clock} />
            <StatCard title="Total Faltas" value={totalFaltas} icon={AlertTriangle} />
          </div>

          {totalOs > 0 && totalPendentes === 0 && (
            <NextStepBanner
              variant="success"
              title="Próximo passo: gerar demonstrativos da competência"
              description={
                <>
                  Todas as DREs concluíram a apuração de {mesLabel}. Avance para os demonstrativos
                  por credenciado e o fechamento mensal no Financeiro.
                </>
              }
              actionLabel="Ir para Demonstrativos"
              onAction={() => navegar(`/operacao/financeiro/demonstrativos?mes=${mes}`)}
            />
          )}

          <FilterPresets
            currentValues={{ status: filtroStatus }}
            onApply={(valores) => {
              definirFiltroBusca('');
              definirFiltroStatus(valores.status ?? '');
            }}
            presets={
              [
                {
                  id: 'pendentes',
                  label: 'Com pendências',
                  badge: dresPendentes,
                  values: { status: 'pendente' },
                },
                {
                  id: 'concluidas',
                  label: 'Concluídas',
                  badge: dresConcluidas,
                  values: { status: 'concluida' },
                },
                { id: 'todos', label: 'Todas as DREs', values: { status: '' } },
              ] satisfies FilterPreset[]
            }
          />

          <FilterBar
            fields={camposFiltro}
            values={{ busca: filtroBusca, status: filtroStatus }}
            onChange={(chave, valor) => {
              if (chave === 'busca') definirFiltroBusca(valor);
              if (chave === 'status') definirFiltroStatus(valor);
            }}
            onClear={() => {
              definirFiltroBusca('');
              definirFiltroStatus('');
            }}
          />

          {isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
            />
          ) : (
            <DataTable
              columns={tableView.orderedColumns}
              personalizacao={tableView}
              data={linhas}
              isLoading={isLoading}
              onRowClick={(linha) =>
                navegar(`/operacao/financeiro/apuracao-mensal/${mes}/${linha.dreId}`)
              }
              emptyTitle={`Sem OS ativas em ${mesLabel}`}
              emptyDescription="Verifique se a competência tem termos ativos ou avance para o mês seguinte para iniciar o lançamento."
              ariaLabel="Apuração por DRE"
            />
          )}
        </TabsContent>

        <TabsContent value="acompanhamento">
          <AcompanhamentoMensalTable mes={mes} />
        </TabsContent>

        <TabsContent value="medicoes" className="space-y-4">
          {medicoes.isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{
                label: 'Tentar novamente',
                onClick: () => void medicoes.refetch(),
              }}
            />
          ) : (
            <DataTable
              columns={
                [
                  { header: 'Credenciado', accessorKey: 'credenciadoNome' },
                  {
                    header: 'Status',
                    cell: (l) => (
                      <StatusBadge
                        status="pendente"
                        label={ROTULO_STATUS_MEDICAO[l.status] ?? l.status}
                      />
                    ),
                  },
                  {
                    header: 'Valor total',
                    cell: (l) => <MoneyDisplay value={l.valorTotal} />,
                    cellClassName: 'text-right',
                    headerClassName: 'text-right',
                  },
                ] as DataTableColumn<MedicaoCicloLinha & { id: string }>[]
              }
              data={(medicoes.data?.dados ?? []).map((l) => ({ ...l, id: l.id }))}
              isLoading={medicoes.isLoading}
              onRowClick={(l) => navegar(`/operacao/financeiro/medicoes/${l.id}`)}
              emptyTitle="Nenhuma medição encontrada"
              emptyDescription="Não há medições geradas para esta competência."
              ariaLabel="Medições"
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ApuracaoMensalPage;
